import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

import { slimInitFirestore, slimInitMongo } from "../utils/initFirestore";
import { usePrevious } from "../utils/usePrevious";
import { useAuthContext } from "./AuthContext";

type ModelsDBContextType = {
  modelsStoreReady: boolean;
};

const modelsDBContextType = createContext<ModelsDBContextType>({ modelsStoreReady: false });

export const useModelsStore = () => useContext(modelsDBContextType);

export const ModelsContextProvider = ({ children }: { children?: ReactNode }) => {
  const { currentUser } = useAuthContext();
  const prevCurrentUser = usePrevious(currentUser);
  const [modelsStoreReady, setModelsStoreReady] = useState(false);

  useEffect(() => {
    if (currentUser === prevCurrentUser) {
      return;
    }

    if (currentUser) {
      currentUser.getIdTokenResult(false).then((idTokenResult) => {
        if (process.env.REACT_APP_MONGO_BACKEND) {
          slimInitMongo();
        } else {
          slimInitFirestore({ idToken: idTokenResult });
        }
        setModelsStoreReady(true);
      });
    } else {
      setModelsStoreReady(false);
    }
  }, [currentUser, prevCurrentUser]);

  return <modelsDBContextType.Provider value={{ modelsStoreReady }}>{children}</modelsDBContextType.Provider>;
};

export const ModelsStoreGuard = ({ children }: { children: ReactNode }) => {
  const { modelsStoreReady } = useModelsStore();

  if (!modelsStoreReady) {
    return null;
  }

  return <>{children}</>;
};
